import * as React from 'react';
import {useMemo} from 'react';
import {Box, Grid} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useFormik} from 'formik';
import {SelectPeriodDialogProps, useStyles} from './SelectPeriodDialog.utils';
import {ReduxState} from '../../../store/types';
import PermissionProvider from '../../../components/PermissionProvider/PermissionProvider';
import DialogContainer, {DialogButton} from '../../../components/AlertDialog/DialogContainer';
import {Permission} from '../../../store/types/Permission';
import CustomKeyboardDateTimePicker from '../../../components/KeyboardDateTimePicker/KeyboardDateTimePicker';
import {convertCurrentDateToUserTimeZone} from '../../PortalUsers/utils';
import {dateFromDateTillValidationSchema} from '../../../components/Extensions/CallHistoryDialog.utils';
import classNames from 'classnames';

const SelectPeriodDialog: React.FC<SelectPeriodDialogProps> = ({
        from,
        till,
        isOpen,
        onUpdate,
        clickOnClose
    }) => {
    const {t} = useTranslation();
    const classes = useStyles();
    
    const userDateTimeFormat = useSelector<ReduxState, string>(
        (state) =>
            state.generic.globalCustomerInfo?.customer_info
                ?.out_date_time_format || '',
    );

    const initialValues: SelectPeriodDialogProps = useMemo(
        () => ({
            from: from,
            till: till
        } as SelectPeriodDialogProps),
        [from, till],
    );

    const keepMonthConfigValue = useSelector<ReduxState, number>(
        (state) => state.generic?.configData?.Metrics?.CallActivityMetricsKeepMonth ?? 0,
    );

    const {timeZonesList} = useSelector(
        (state: ReduxState) => state.generic,
    );

    const iTimeZone = useSelector(
        (state: ReduxState) => state.myProfile?.customer?.iTimeZone,
    );
    
    const timezoneOffset = useSelector<ReduxState, number>(
        (state) => state.generic.sessionData?.tz_offset || 0,
    );

    const minDateForDateFrom = useMemo(() => {
        if(keepMonthConfigValue > 0)
        {
            const tzname = timeZonesList.find((e) => e.i_time_zone === iTimeZone)
                ?.time_zone_name;
            const currentDate = convertCurrentDateToUserTimeZone(tzname);
            const val = new Date(currentDate.setMonth(currentDate.getMonth() + (-1 * (keepMonthConfigValue ?? 0))));
            const returnValue = new Date(val.getFullYear(), val.getMonth(), val.getDate());
            return returnValue;
        }
        else
        {
            return undefined;
        }
    }, [timeZonesList, iTimeZone, keepMonthConfigValue]);

    const {
        values,
        handleSubmit,
        resetForm,
        setFieldError,
        setFieldValue,
        errors
    } = useFormik<SelectPeriodDialogProps>({
        initialValues,
        onSubmit: (form) => {
            onUpdate?.(form.from, form.till);
            clickOnClose?.();
        },
        validationSchema: dateFromDateTillValidationSchema(timezoneOffset, userDateTimeFormat),
        enableReinitialize: true,
        validateOnChange: false,
        validateOnBlur: true
    });

    return (
        <PermissionProvider permission={Permission.Calls.Settings.CallScreening.value}>
            <DialogContainer
                className={classes.modalContainer}
                isOpen={isOpen}
                dataQa={'select-period-modal'}
                header={'Select custom date range'}
                headerClass={classes.header}
                dialogActionsButtons={[
                    <DialogButton
                        key="cancel"
                        label={t('common:cancel')}
                        onClick={() => {
                            resetForm();
                            clickOnClose?.();
                        }}
                    />,
                    <DialogButton
                        key="save"
                        label={t('common:apply')}
                        onClick={handleSubmit}
                        dataTestId={'apply-select-period-button'}
                        primary
                    />,
                ]}
            >
                <form
                    className={classes.form}
                    autoComplete="off"
                    data-testid="add-new-interval-form"
                >
                    <Grid className={classes.itemsContainer}>
                        <Box className={classes.boxRow}>
                            <CustomKeyboardDateTimePicker
                                label={t('common:fromDate')}
                                id={'from'}
                                value={values.from}
                                onChange={(v) => {setFieldValue('from', v);}}
                                dataQa={'select-by-from-date'}
                                dataTestId={'select-by-from-Date'}
                                userDateTimeFormat={userDateTimeFormat}
                                setFieldError={setFieldError}
                                key="from"
                                helperText={errors.from}
                                className={classes.filter}
                                minDate={minDateForDateFrom}
                                skipPermission
                            />
                        </Box>

                        <Box className={classes.boxRow}>
                            <CustomKeyboardDateTimePicker
                                label={t('common:toDate')}
                                id={'till'}
                                value={values.till}
                                onChange={(v) => {setFieldValue('till', v);}}
                                dataQa={'select-by-to-date'}
                                dataTestId={'select-by-to-date'}
                                userDateTimeFormat={userDateTimeFormat}
                                key="till"
                                setFieldError={setFieldError}
                                helperText={errors.till}
                                className={classNames(classes.filter, classes.notLastFilter)}
                                skipPermission
                            />
                        </Box>

                    </Grid>
                </form>
            </DialogContainer>
        </PermissionProvider>
    );
};

export default SelectPeriodDialog;